<template>
  <div>
    <CRow>
      <CCol sm="12">
        <CCard v-if="restaurant">
          <CCardHeader>
            <CIcon name="cil-list-rich" />
            <h5 class="d-inline ml-2">
              <h3 class="d-inline">
                <CBadge color="secondary">{{ restaurant.restaurant_name }}</CBadge>
              </h3>
              Option Groups
            </h5>
            <div class="card-header-actions d-flex flex-gap-2">
              <CButton color="primary" @click="openOptionGroupModal(null)">
                <CIcon name="cil-plus" class="align-bottom" /><strong>Add New</strong> Option Group
              </CButton>

              <CDropdown color="dark" toggler-text="Back" placement="bottom-end">
                <template #toggler>
                  <CButton color="dark">
                    <CIcon name="cil-arrow-thick-left" class="align-bottom" /> Back
                    <CIcon name="cil-chevron-bottom" class="ml-1" />
                  </CButton>
                </template>

                <CDropdownItem
                  v-if="restaurantId"
                  :to="{ name: 'RestaurantMenuV2List', params: { restaurant_id: restaurantId } }"
                >
                  <CIcon name="cil-dinner" class="mr-2" /> Back To Menu Items
                </CDropdownItem>

                <CDropdownItem v-if="restaurantId" :to="{ name: 'View Restaurant', params: { id: restaurantId } }">
                  <CIcon name="cil-restaurant" class="mr-2" /> Back To Restaurant
                </CDropdownItem>

                <CDropdownItem :to="prevRoute">
                  <CIcon name="cil-arrow-left" class="mr-2" /> Previous Page
                </CDropdownItem>
              </CDropdown>
            </div>
          </CCardHeader>

          <CCardBody>
            <!-- Filters -->
            <CCard class="filters" accent-color="warning">
              <CCardHeader role="button" class="text-warning shadow-none card-header" @click="resetFilters">
                <strong class="m-0"
                  ><CIcon :name="`cil-filter${$store.state.filterShow ? '-x' : ''}`" /> Filters</strong
                >
                <div class="card-header-actions">
                  <CIcon :name="`cil-chevron-${$store.state.filterShow ? 'bottom' : 'top'}`" />
                </div>
              </CCardHeader>

              <CCollapse :show="$store.state.filterShow">
                <CCardBody class="p-2">
                  <div class="d-flex flex-md-row flex-column" style="gap: 0.5rem">
                    <!-- Restaurant Filter -->
                    <div class="flex-grow-1">
                      <CInput
                        class="mb-0"
                        type="search"
                        placeholder="Search by name..."
                        v-model="searchQuery"
                        @input="searchOptionGroups"
                      />
                    </div>
                  </div>
                </CCardBody>
              </CCollapse>
            </CCard>

            <CCard
              v-if="restaurant?.usetoprice"
              class="price-sync-card"
              @click="() => (samepricetoprice = !samepricetoprice)"
              role="button"
            >
              <CCardBody class="p-3">
                <div class="d-flex align-items-center">
                  <div class="mr-3">
                    <CIcon name="cil-link" :class="samepricetoprice ? 'text-primary' : 'text-muted'" size="xl" />
                  </div>
                  <div class="flex-grow-1">
                    <h6 class="mb-1">Sync Delivery and Takeout Prices</h6>
                    <small class="text-muted"
                      >When enabled, takeout prices will automatically match delivery prices</small
                    >
                  </div>
                  <div>
                    <CSwitch
                      class="mx-1"
                      color="primary"
                      shape="pill"
                      size="lg"
                      :checked.sync="samepricetoprice"
                      @update:checked="() => (samepricetoprice = !samepricetoprice)"
                      label
                    />
                  </div>
                </div>
              </CCardBody>
            </CCard>

            <!-- Option Groups List -->
            <div v-if="loading" class="text-center py-5">
              <CSpinner color="primary" />
            </div>

            <div v-else-if="optionGroups.length === 0" class="text-center py-5">
              <h5 class="text-muted">⚠ No option groups found.</h5>
              <CButton color="success" @click="openOptionGroupModal(null)" class="mt-3">
                <CIcon name="cil-plus" /> Add First Option Group
              </CButton>
            </div>

            <div v-else class="option-groups-container">
              <CCard v-for="group in optionGroups" :key="group.id" class="option-group-card">
                <CCardHeader
                  class="d-flex align-items-center bg-light option-group-header"
                  @click="toggleOptionsVisibility(group.id)"
                >
                  <div class="group-info">
                    <h5 class="mb-0">#{{ group.id}} {{ group.name }}</h5>
                    <small v-if="group.description" class="d-block text-muted">{{ group.description }}</small>

                    <div class="d-flex align-items-center flex-gap-1 mt-2">
                      <span class="badge badge-primary"> Min: {{ group.min_selections || 0 }} </span>
                      <span class="badge badge-primary"> Max: {{ group.max_selections || 'All' }} </span>
                      <span class="small text-muted">
                        {{ selectionTypeDescription(group.min_selections, group.max_selections) }}
                      </span>
                    </div>
                  </div>

                  <div class="ml-auto d-flex align-items-center actions-container">
                    <!-- Show/Hide Indicator -->
                    <CIcon
                      :name="isGroupExpanded(group.id) ? 'cil-chevron-top' : 'cil-chevron-bottom'"
                      class="mr-2 toggle-icon"
                      :class="{ expanded: isGroupExpanded(group.id) }"
                    />

                    <!-- Options Count Badge -->
                    <CBadge color="info" class="mr-2"> {{ group.options ? group.options.length : 0 }} Options </CBadge>

                    <!-- Actions dropdown -->
                    <CDropdown @click.native.stop>
                      <template #toggler>
                        <CButton color="ghost" size="sm">
                          <CIcon name="cil-options" />
                        </CButton>
                      </template>
                      <CDropdownItem @click="openOptionGroupModal(group)">
                        <CIcon name="cil-pencil" class="mr-2" /> Edit Group
                      </CDropdownItem>
                      <CDropdownItem @click="cloneOptionGroup(group.id)">
                        <CIcon name="cil-copy" class="mr-2" /> Clone Group
                      </CDropdownItem>
                      <CDropdownDivider class="my-1" />
                      <CDropdownItem @click="openOptionModal(null, group.id)">
                        <CIcon name="cil-plus" class="mr-2" /> Add Option
                      </CDropdownItem>
                      <CDropdownDivider class="my-1" />
                      <CDropdownItem @click="deleteOptionGroup(group.id)" class="text-danger">
                        <CIcon name="cil-trash" class="mr-2" /> Delete Group
                      </CDropdownItem>
                    </CDropdown>
                  </div>
                </CCardHeader>

                <!-- Options List - Shown when expanded -->
                <CCollapse :show="isGroupExpanded(group.id)">
                  <CCardBody>
                    <OptionsList
                      :key="`options-list-${group.id}-${refreshTrigger}`"
                      :options.sync="group.options"
                      :option-group-id="group.id"
                      :usetoprice="restaurant?.usetoprice"
                      :samepricetoprice="samepricetoprice"
                      @edit-option="openOptionModal"
                      @delete-option="deleteOption"
                      @refresh-data="getOptionGroups"
                      @options-reordered="updateOptionsOrder(group.id, $event)"
                    />
                  </CCardBody>
                </CCollapse>
              </CCard>
            </div>
          </CCardBody>
        </CCard>

        <div v-else>
          <CSpinner v-if="loading" color="primary" />
          <h6 v-else>Restaurant not found!</h6>
        </div>
      </CCol>
    </CRow>

    <!-- Option Group Modal -->
    <CModal :show.sync="optionGroupModal" title="Option Group" size="lg" :closeOnBackdrop="false">
      <CForm @submit.prevent="saveOptionGroup" class="form-style-1">
        <CInput
          label="Name"
          placeholder="Option Group Name"
          v-model="optionGroupForm.name"
          :is-valid="optionGroupForm.name.length >= 2"
          invalid-feedback="Name is required (min 2 characters)"
          class="mb-3"
        />

        <CTextarea
          label="Description"
          placeholder="Optional description"
          v-model="optionGroupForm.description"
          rows="3"
          class="mb-3"
        />

        <CTabs class="tab-menu" :active-tab.sync="activeTab" variant="pills" :vertical="{ navs: 'col-md-2', content: 'col-md-10' }">
          <CTab title="Selection Requirements" active>
            <div class="selection-requirements-panel">
              <label class="selection-section-label">Selection Requirements</label>

              <!-- Is selection required? -->
              <div class="selection-question mb-3">
                <label>Are customers required to select at least one option?</label>
                <CButton
                  v-for="(option, index) in [
                    { text: 'Yes', value: 1 },
                    { text: 'No', value: 0 },
                  ]"
                  :key="index"
                  :color="optionGroupForm.min_selections === option.value ? 'primary' : 'secondary'"
                  class="selection-button mr-2"
                  @click="setMinSelections(option.value)"
                >
                  {{ option.text }}
                </CButton>
                <small class="form-text text-muted">
                  {{
                    optionGroupForm.min_selections > 0
                      ? 'Customers must select at least one option'
                      : 'Selection is optional'
                  }}
                </small>
              </div>

              <!-- Max selection limits -->
              <div class="selection-question">
                <label>How many options can customers select at maximum?</label>
                <div class="max-selection-buttons-container">
                  <CButton
                    color="secondary"
                    class="selection-button max-option-all mr-2 mb-2"
                    :color="optionGroupForm.max_selections === null ? 'primary' : 'secondary'"
                    @click="setMaxSelections(null)"
                  >
                    All Options
                  </CButton>
                  <CButton
                    v-for="num in 5"
                    :key="`max-option-${num}`"
                    class="selection-button max-option-num mr-2 mb-2"
                    :color="optionGroupForm.max_selections === num ? 'primary' : 'secondary'"
                    @click="setMaxSelections(num)"
                  >
                    {{ num }}
                  </CButton>
                  <CButton
                    v-if="!customMaxInput"
                    class="selection-button mb-2"
                    color="info"
                    variant="outline"
                    @click="toggleCustomMaxInput"
                  >
                    <CIcon name="cil-plus" /> Custom
                  </CButton>

                  <CInput
                    v-else
                    type="number"
                    class="custom-max-input"
                    placeholder="Custom.."
                    :min="optionGroupForm.min_selections"
                    v-model="optionGroupForm.max_selections"
                    @input="validateMaxSelections"
                  />
                </div>
                <small class="form-text text-muted">
                  {{ maxSelectionsDescription }}
                </small>

                <div class="alert alert-info d-flex align-items-center mt-3 mb-0 flex-gap-2">
                  <CIcon name="cil-info" />
                  <div>
                    Selection type:
                    <strong>{{
                      selectionTypeDescription(optionGroupForm.min_selections, optionGroupForm.max_selections)
                    }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </CTab>

          <CTab title="Add Menu Items as Options">
            <AddMenuItemsAsOptions
              :restaurant-id="restaurantId"
              :selected-items.sync="optionGroupForm.options"
            />
          </CTab>
        </CTabs>
      </CForm>

      <template #footer>
        <CButton color="secondary" @click="optionGroupModal = false">Cancel</CButton>
        <CButton color="primary" @click="saveOptionGroup" :disabled="!canSaveOptionGroup || submitting">
          <CSpinner v-if="submitting" size="sm" color="light" />
          {{ editingOptionGroup ? 'Update' : 'Create' }}
        </CButton>
      </template>
    </CModal>

    <!-- Option Modal -->
    <CModal :show.sync="optionModal" :title="`Option - ${currentGroupName}`" :closeOnBackdrop="false">
      <CForm @submit.prevent="saveOption" class="form-style-1">
        <CInput
          label="Name"
          placeholder="Option Name"
          v-model="optionForm.name"
          :is-valid="optionForm.name.length >= 2"
          invalid-feedback="Name is required (min 2 characters)"
          class="mb-3"
        />

        <CSelect
          label="Item Type"
          placeholder="Please choose a item type"
          :options="this.$globalVar.menuTypes"
          :value.sync="optionForm.type_id"
          class="mb-3"
        />

        <CTextarea
          label="Description"
          placeholder="Optional description"
          v-model="optionForm.description"
          rows="3"
          class="mb-3"
        />

        <CRow>
          <CCol sm="6">
            <CInput
              label="Price"
              type="number"
              step="0.10"
              v-model="optionForm.price"
              placeholder="0.00"
              class="mb-3"
              :disabled="optionForm.sold_out"
            >
              <template #prepend-content><CIcon name="cil-euro" /></template>
            </CInput>
          </CCol>
          <CCol sm="6" v-if="restaurant?.usetoprice">
            <CInput
              label="Takeout Price"
              type="number"
              step="0.10"
              v-model="optionForm.toPrice"
              placeholder="0.00"
              class="mb-3"
              :disabled="optionForm.sold_out || samepricetoprice"
            >
              <template #prepend-content><CIcon name="cil-euro" /></template>
            </CInput>
          </CCol>
        </CRow>

        <div class="form-group mb-0">
          <label class="d-block">Sold Out:</label>
          <CSwitch
            :checked.sync="optionForm.sold_out"
            labelOn="YES"
            labelOff="NO"
            color="danger"
            shape="pill"
            size="lg"
          />
        </div>
      </CForm>

      <template #footer>
        <CButton color="secondary" @click="optionModal = false">Cancel</CButton>
        <CButton color="primary" @click="saveOption" :disabled="!canSaveOption || submitting">
          <CSpinner v-if="submitting" size="sm" color="light" />
          {{ editingOption ? 'Update' : 'Create' }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import _debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';
import OptionsList from './OptionsList.vue';
import AddMenuItemsAsOptions from './components/AddMenuItemsAsOptions.vue';

export default {
  name: 'OptionGroups',
  components: {
    OptionsList,
    AddMenuItemsAsOptions,
  },
  data() {
    return {
      restaurant: null,
      loading: true,
      submitting: false,

      optionGroups: [],
      searchQuery: '',
      expandedGroups: [],
      refreshTrigger: 0,

      // Option Group Modal
      optionGroupModal: false,
      activeTab: 0,
      currentGroupName: '',
      optionGroupForm: {
        name: '',
        description: '',
        min_selections: 0,
        max_selections: null,
        options: [],
      },
      editingOptionGroup: null,
      customMaxInput: false,

      // Option Modal
      optionModal: false,
      optionForm: {
        type_id: 1,
        name: '',
        description: '',
        price: 0,
        toPrice: 0,
        sold_out: false,
        group_id: null,
      },
      editingOption: null,

      // Previous page
      prevRoute: null,

      samepricetoprice: false,
    };
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.prevRoute = from;
    });
  },

  computed: {
    ...mapGetters(['errors']),
    restaurantId() {
      return this.$route.params.restaurant_id;
    },
    maxSelectionsDescription() {
      const max = this.optionGroupForm.max_selections;
      if (max === null) return 'Customers can select any number of options';
      if (max === 1) return 'Customers can select only one option';
      return `Customers can select up to ${max} options`;
    },
    canSaveOptionGroup() {
      const { name, min_selections, max_selections } = this.optionGroupForm;
      return name.length >= 2 && min_selections !== '' && (max_selections === null || min_selections <= max_selections);
    },
    canSaveOption() {
      return this.optionForm.name.length >= 2;
    },
  },

  async mounted() {
    await this.getRestaurant();
    await this.getOptionGroups();
  },

  methods: {
    async getRestaurant() {
      try {
        const { data } = await this.$axios.get(this.$backend.RESTAURANT.GET_NAME.replace('{id}', this.restaurantId), {
          params: { fields: 'usetoprice' },
        });
        this.restaurant = data;
      } finally {
        this.loading = false;
      }
    },

    resetFilters() {
      if (this.$store.state.filterShow) {
        this.searchQuery = '';
        this.getOptionGroups();
      }
      this.$store.commit('toggle', 'filterShow');
    },

    handleErrors(error) {
      if (typeof this.errors === 'string') {
        this.$toast.error(this.errors);
      } else if (typeof this.errors === 'object' && Object.values(this.errors).length > 0) {
        this.$toast.error(Object.values(this.errors).join());
      } else {
        this.$toast.error(error.message);
      }
    },

    // Option Groups
    async getOptionGroups(loading = true) {
      this.loading = loading;
      await this.$axios
        .get(this.$backend.MENU_V2.GET_OPTION_GROUPS(this.restaurantId), {
          params: {
            search: this.searchQuery,
          },
        })
        .then((response) => {
          this.optionGroups = response.data;
        })
        .catch((error) => {
          this.handleErrors(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    searchOptionGroups: _debounce(function () {
      this.getOptionGroups();
    }, 300),

    async saveOptionGroup() {
      if (!this.canSaveOptionGroup) return;

      this.submitting = true;

      try {
        let response;

        if (this.editingOptionGroup) {
          // Update existing option group
          response = await this.$axios.put(
            this.$backend.MENU_V2.UPDATE_OPTION_GROUP(this.restaurantId, this.editingOptionGroup.id),
            this.optionGroupForm
          );

          this.$toast.success(`Option group "${this.optionGroupForm.name}" has been updated.`);
        } else {
          // Create new option group
          response = await this.$axios.post(
            this.$backend.MENU_V2.CREATE_OPTION_GROUP(this.restaurantId),
            this.optionGroupForm
          );

          this.$toast.success(`Option group "${this.optionGroupForm.name}" has been created.`);
        }

        this.optionGroupModal = false;
        await this.getOptionGroups();
      } catch (error) {
        this.handleErrors(error);
      } finally {
        this.submitting = false;
      }
    },

    async cloneOptionGroup(groupId) {
      this.$confirm({
        title: 'Confirm Clone',
        message: 'Are you sure you want to clone this option group with all its options?',
        button: {
          no: 'Cancel',
          yes: 'Yes, Clone It',
        },
        callback: async (confirm) => {
          if (confirm) {
            this.submitting = true;

            try {
              await this.$axios.post(this.$backend.MENU_V2.CLONE_OPTION_GROUP(this.restaurantId, groupId));

              this.$toast.success('Option group has been cloned with all its options.');
              await this.getOptionGroups();
            } catch (error) {
              this.handleErrors(error);
            } finally {
              this.submitting = false;
            }
          }
        },
      });
    },

    async deleteOptionGroup(groupId) {
      this.$confirm({
        title: 'Confirm Delete',
        message: 'Are you sure you want to delete this option group? This will also delete all options in this group.',
        button: {
          no: 'Cancel',
          yes: 'Yes, Delete It',
        },
        callback: async (confirm) => {
          if (confirm) {
            this.submitting = true;

            try {
              await this.$axios.delete(this.$backend.MENU_V2.DELETE_OPTION_GROUP(this.restaurantId, groupId));

              this.$toast.success('Option group has been deleted.');
              await this.getOptionGroups();
            } catch (error) {
              this.handleErrors(error);
            } finally {
              this.submitting = false;
            }
          }
        },
      });
    },

    toggleOptionsVisibility(groupId) {
      const index = this.expandedGroups.indexOf(groupId);
      if (index === -1) {
        this.expandedGroups.push(groupId);
      } else {
        this.expandedGroups.splice(index, 1);
      }
    },

    isGroupExpanded(groupId) {
      return this.expandedGroups.includes(groupId);
    },

    openOptionGroupModal(group) {
      this.editingOptionGroup = group;
      this.customMaxInput = false;
      this.activeTab = 0;

      if (group) {
        this.optionGroupForm = {
          name: group.name,
          description: group.description || '',
          min_selections: group.min_selections,
          max_selections: group.max_selections,
          options: [],
        };
        // If max_selections exists and is greater than 5, show the custom input
        if (this.optionGroupForm.max_selections > 5) {
          this.customMaxInput = true;
        }
      } else {
        this.optionGroupForm = {
          name: '',
          description: '',
          min_selections: 0,
          max_selections: null,
          options: [],
        };
      }

      this.optionGroupModal = true;
    },

    toggleCustomMaxInput() {
      this.customMaxInput = !this.customMaxInput;
      if (this.customMaxInput) {
        // If we're showing the custom input and max is null or ≤ 5, set a default value
        if (this.optionGroupForm.max_selections === null || this.optionGroupForm.max_selections <= 5) {
          this.optionGroupForm.max_selections = Math.max(6, this.optionGroupForm.min_selections + 1);
        }
      }
    },

    validateMaxSelections() {
      // Ensure max_selections is at least min_selections
      if (
        this.optionGroupForm.max_selections !== null &&
        this.optionGroupForm.max_selections < this.optionGroupForm.min_selections
      ) {
        this.optionGroupForm.max_selections = this.optionGroupForm.min_selections;
      }
    },

    setMinSelections(value) {
      this.optionGroupForm.min_selections = value;

      // If min is set to greater than max, adjust max accordingly
      if (
        this.optionGroupForm.max_selections !== null &&
        this.optionGroupForm.min_selections > this.optionGroupForm.max_selections
      ) {
        this.optionGroupForm.max_selections = this.optionGroupForm.min_selections;
      }
    },

    setMaxSelections(value) {
      this.optionGroupForm.max_selections = value;
      // Hide the custom input when selecting preset values
      this.customMaxInput = false;
    },

    selectionTypeDescription(min, max) {
      if (max === 1) {
        return 'Single selection';
      } else {
        return 'Multiple selections';
      }
    },

    // Options
    openOptionModal(option, groupId) {
      this.editingOption = option;

      // Find the group name for the current group
      const group = this.optionGroups.find((g) => g.id === (option ? option.group_id : groupId));
      this.currentGroupName = group ? group.name : '';

      if (option) {
        this.optionForm = {
          type_id: option.type_id,
          name: option.name,
          description: option.description || '',
          price: option.price,
          toPrice: option.toPrice,
          sold_out: option.sold_out,
          group_id: option.group_id,
        };
      } else {
        this.optionForm = {
          type_id: 1,
          name: '',
          description: '',
          price: 0,
          toPrice: 0,
          sold_out: false,
          group_id: groupId,
        };
      }

      this.optionModal = true;
    },

    async saveOption() {
      if (!this.canSaveOption) return;

      this.submitting = true;

      try {
        if (this.optionForm.price === '') {
          this.optionForm.price = 0;
        }

        if (this.optionForm.toPrice === '') {
          this.optionForm.toPrice = 0;
        }

        if (this.restaurant?.usetoprice && this.samepricetoprice) {
          this.optionForm.toPrice = this.optionForm.price;
        }

        if (this.editingOption) {
          // Update existing option
          await this.$axios.put(
            this.$backend.MENU_V2.UPDATE_OPTION_GROUP_OPTION(
              this.restaurantId,
              this.optionForm.group_id,
              this.editingOption.id
            ),
            this.optionForm
          );

          this.$toast.success(`Option "${this.optionForm.name}" has been updated.`);
        } else {
          // Create new option
          await this.$axios.post(
            this.$backend.MENU_V2.CREATE_OPTION_GROUP_OPTION(this.restaurantId, this.optionForm.group_id),
            this.optionForm
          );

          this.$toast.success(`Option "${this.optionForm.name}" has been created.`);
        }

        this.optionModal = false;
        await this.getOptionGroups();
      } catch (error) {
        this.handleErrors(error);
      } finally {
        this.submitting = false;
      }
    },

    async deleteOption(optionId, groupId) {
      this.$confirm({
        title: 'Confirm Delete',
        message: 'Are you sure you want to delete this option?',
        button: {
          no: 'Cancel',
          yes: 'Yes, Delete It',
        },
        callback: async (confirm) => {
          if (confirm) {
            this.submitting = true;

            try {
              await this.$axios.delete(
                this.$backend.MENU_V2.DELETE_OPTION_GROUP_OPTION(this.restaurantId, groupId, optionId)
              );

              this.$toast.success('Option has been deleted.');
              await this.getOptionGroups();
            } catch (error) {
              this.handleErrors(error);
            } finally {
              this.submitting = false;
            }
          }
        },
      });
    },

    updateOptionsOrder(groupId, updatedOptions) {
      // Find the group in the array
      const groupIndex = this.optionGroups.findIndex((g) => g.id === groupId);

      if (groupIndex !== -1) {
        // First try a direct update with Vue's reactivity system
        this.$set(this.optionGroups[groupIndex], 'options', updatedOptions);

        // Then force a refresh of the component
        this.refreshTrigger++;
      }
    },
  },
};
</script>

<style>
@media (max-width: 767px) {
  .tab-menu > div:first-child {
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #d8dbe0;
  }
}
</style>

<style scoped>
.option-group-card {
  border-left: 3px solid #20a8d8;
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

.option-group-card:last-child {
  margin-bottom: 0;
}

.option-group-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.option-group-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.option-group-header:hover {
  background-color: #e8f4f8 !important;
}

.toggle-icon {
  font-size: 1.1rem;
  color: #777;
  transition: transform 0.3s ease, color 0.3s ease;
}

.toggle-icon.expanded {
  color: #20a8d8;
}

.option-groups-container {
  margin-top: 20px;
}

/* Selection requirements styles */
.selection-requirements-panel {
  border-left: 3px solid #20a8d8;
  padding: 15px;
  background-color: #f8f9fa;
}

.selection-section-label {
  font-weight: bold;
  font-size: 1.1rem;
  color: #20a8d8;
  margin-bottom: 15px;
  display: block;
}

.selection-question label {
  display: block;
  font-weight: 600;
  margin-bottom: 10px;
}

.selection-button {
  min-width: 80px;
  margin-bottom: 8px;
  height: 38px;
}

.max-option-num {
  min-width: 52px;
  padding-left: 0;
  padding-right: 0;
}

.max-option-all {
  min-width: 90px;
}

.custom-max-input {
  width: 100px;
  margin-bottom: 8px;
  height: 38px;
}

.max-selection-buttons-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.price-sync-card {
  transition: all 0.2s ease;
  border-left: 3px solid #20a8d8;
  margin-bottom: 1rem;
  cursor: pointer;
}

.price-sync-card:hover {
  background-color: #f8f9fa;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}
</style>
